import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import { useToast } from "@/components/ToastProvider";
import { useArtistScope } from "@/lib/artistContext";
import { fetchMyArtist, updateMyArtist, uploadArtistBandPhoto, uploadArtistLogo } from "@/api/artistSettings";
import type { ArtistMember, ArtistSettingsPayload } from "@/types/artistSettings";

const emptyBillingAddress = {
  line1: "",
  line2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "",
};

const emptyMember: ArtistMember = { name: "", role: "" };

export default function ArtistSettingsPage() {
  const toast = useToast();
  const { artists, selectedArtistId, selectArtist, isLoading: artistLoading, isMultiArtist } = useArtistScope();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [assetError, setAssetError] = useState<string | null>(null);
  const [uploadingBandPhoto, setUploadingBandPhoto] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form, setForm] = useState({
    name: "",
    genres: "",
    home_city: "",
    home_region: "",
    home_country: "",
    bio: "",
    website: "",
    instagram: "",
    facebook: "",
    x_url: "",
    youtube_url: "",
    tiktok: "",
    spotify_url: "",
    apple_music_url: "",
    bandcamp_url: "",
    bandsintown_url: "",
    bandsintown_identifier: "",
    band_photo_url: "",
    logo_url: "",
    booking_email: "",
    billing_name: "",
    tax_id: "",
  });
  const [videoLinks, setVideoLinks] = useState<string[]>(["", "", ""]);
  const [audioLinks, setAudioLinks] = useState<string[]>(["", "", ""]);
  const [members, setMembers] = useState<ArtistMember[]>([{ ...emptyMember }]);
  const [billingAddress, setBillingAddress] = useState({ ...emptyBillingAddress });

  const loadArtist = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const payload = await fetchMyArtist();
      setForm({
        name: payload.name ?? "",
        genres: payload.genres?.join(", ") ?? "",
        home_city: payload.home_city ?? "",
        home_region: payload.home_region ?? "",
        home_country: payload.home_country ?? "",
        bio: payload.bio ?? "",
        website: payload.website ?? "",
        instagram: payload.instagram ?? "",
        facebook: payload.facebook ?? "",
        x_url: payload.x_url ?? "",
        youtube_url: payload.youtube_url ?? "",
        tiktok: payload.tiktok ?? "",
        spotify_url: payload.spotify_url ?? "",
        apple_music_url: payload.apple_music_url ?? "",
        bandcamp_url: payload.bandcamp_url ?? "",
        bandsintown_url: payload.bandsintown_url ?? "",
        bandsintown_identifier: payload.bandsintown_identifier ?? "",
        band_photo_url: payload.band_photo_url ?? "",
        logo_url: payload.logo_url ?? "",
        booking_email: payload.booking_email ?? "",
        billing_name: payload.billing_name ?? "",
        tax_id: payload.tax_id ?? "",
      });
      const nextVideoLinks = Array.from({ length: 3 }, (_, index) => payload.video_links?.[index] ?? "");
      const nextAudioLinks = Array.from({ length: 3 }, (_, index) => payload.audio_links?.[index] ?? "");
      setVideoLinks(nextVideoLinks);
      setAudioLinks(nextAudioLinks);
      setMembers(
        payload.members.length
          ? payload.members.map((member) => ({
              name: member.name ?? "",
              role: member.role ?? "",
            }))
          : [{ ...emptyMember }],
      );
      setBillingAddress({
        line1: payload.billing_address?.line1 ?? "",
        line2: payload.billing_address?.line2 ?? "",
        city: payload.billing_address?.city ?? "",
        region: payload.billing_address?.region ?? "",
        postal_code: payload.billing_address?.postal_code ?? "",
        country: payload.billing_address?.country ?? "",
      });
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Unable to load artist profile.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadArtist();
  }, [loadArtist, selectedArtistId]);

  const canSave = useMemo(() => Boolean(form.name.trim()), [form.name]);

  const handleFormChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMemberChange = (index: number, field: keyof ArtistMember, value: string) => {
    setMembers((prev) =>
      prev.map((member, idx) => (idx === index ? { ...member, [field]: value } : member)),
    );
  };

  const addMember = () => setMembers((prev) => [...prev, { ...emptyMember }]);

  const handleBillingChange = (field: keyof typeof billingAddress, value: string) => {
    setBillingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setFormError(null);
    setStatusMessage(null);
    try {
      const genres = form.genres
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean);
      const cleanMembers = members
        .map((member) => ({ name: member.name?.trim() ?? "", role: member.role?.trim() ?? "" }))
        .filter((member) => member.name || member.role);
      const payload: ArtistSettingsPayload = {
        name: form.name.trim(),
        genres: genres.length ? genres : undefined,
        home_city: form.home_city.trim() || undefined,
        home_region: form.home_region.trim() || undefined,
        home_country: form.home_country.trim() || undefined,
        bio: form.bio.trim() || undefined,
        members: cleanMembers.length ? cleanMembers : undefined,
        website: form.website.trim() || undefined,
        instagram: form.instagram.trim() || undefined,
        facebook: form.facebook.trim() || undefined,
        x_url: form.x_url.trim() || undefined,
        youtube_url: form.youtube_url.trim() || undefined,
        tiktok: form.tiktok.trim() || undefined,
        spotify_url: form.spotify_url.trim() || undefined,
        apple_music_url: form.apple_music_url.trim() || undefined,
        bandcamp_url: form.bandcamp_url.trim() || undefined,
        bandsintown_url: form.bandsintown_url.trim() || undefined,
        bandsintown_identifier: form.bandsintown_identifier.trim() || undefined,
        video_links: videoLinks.map((link) => link.trim()).filter(Boolean),
        audio_links: audioLinks.map((link) => link.trim()).filter(Boolean),
        booking_email: form.booking_email.trim() || undefined,
        billing_name: form.billing_name.trim() || undefined,
        tax_id: form.tax_id.trim() || undefined,
      };
      if (billingAddress.line1.trim()) {
        payload.billing_address = {
          line1: billingAddress.line1.trim(),
          line2: billingAddress.line2.trim() || undefined,
          city: billingAddress.city.trim(),
          region: billingAddress.region.trim(),
          postal_code: billingAddress.postal_code.trim(),
          country: billingAddress.country.trim(),
        };
      }
      await updateMyArtist(payload);
      setStatusMessage("Artist profile saved.");
      toast.push({ title: "Artist", message: "Artist settings updated." });
      await loadArtist();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to save artist profile.";
      setFormError(message);
      toast.push({ title: "Artist", message });
    } finally {
      setSaving(false);
    }
  };

  const handleVideoLinkChange = (index: number, value: string) => {
    setVideoLinks((prev) => prev.map((entry, idx) => (idx === index ? value : entry)));
  };

  const handleAudioLinkChange = (index: number, value: string) => {
    setAudioLinks((prev) => prev.map((entry, idx) => (idx === index ? value : entry)));
  };

  const handleBandPhotoUpload = async (file: File | null | undefined) => {
    if (!file) return;
    setAssetError(null);
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setAssetError("Band photo must be a PNG, JPG, or WEBP file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setAssetError("Band photo must be 10MB or smaller.");
      return;
    }
    setUploadingBandPhoto(true);
    try {
      const payload = await uploadArtistBandPhoto(file);
      setForm((prev) => ({ ...prev, band_photo_url: payload.band_photo_url ?? "" }));
      toast.push({ title: "Artist assets", message: "Band photo updated." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to upload band photo.";
      setAssetError(message);
      toast.push({ title: "Artist assets", message });
    } finally {
      setUploadingBandPhoto(false);
    }
  };

  const handleLogoUpload = async (file: File | null | undefined) => {
    if (!file) return;
    setAssetError(null);
    if (!["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(file.type)) {
      setAssetError("Logo must be an SVG, PNG, or JPG file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAssetError("Logo must be 5MB or smaller.");
      return;
    }
    setUploadingLogo(true);
    try {
      const payload = await uploadArtistLogo(file);
      setForm((prev) => ({ ...prev, logo_url: payload.logo_url ?? "" }));
      toast.push({ title: "Artist assets", message: "Logo updated." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to upload logo.";
      setAssetError(message);
      toast.push({ title: "Artist assets", message });
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="aa-page-shell space-y-6">
      <PageHeader title="Artist" description="Edit the active artist profile + billing info." />
      <section className="aa-glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Active artist</h2>
            <p className="text-xs text-muted-foreground">The artist profile currently selected in the header.</p>
          </div>
          {isMultiArtist && artists.length > 1 && (
            <div className="text-xs text-muted-foreground">
              <Label htmlFor="artist-settings-selector" className="sr-only">
                Select artist
              </Label>
              <select
                id="artist-settings-selector"
                value={selectedArtistId ?? ""}
                onChange={(event) => selectArtist(event.target.value)}
                disabled={artistLoading || loading}
                className="min-h-[40px] rounded-md border border-border bg-background/40 px-3 py-2 text-sm text-foreground shadow-inner-subtle transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:border-primary/50"
                aria-label="Select artist to manage assets"
              >
                {artists.map((artist) => (
                  <option key={artist.id} value={artist.id}>
                    {artist.name || artist.id}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {loading ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading artist profile…</p>
        ) : loadError ? (
          <p className="mt-4 text-sm text-rose-400">{loadError}</p>
        ) : (
          <form className="mt-5 space-y-5" onSubmit={handleSave}>
            <div>
              <Label>Artist name</Label>
              <Input value={form.name} required onChange={(event) => handleFormChange("name", event.target.value)} />
            </div>
            <div>
              <Label>Genres (comma separated)</Label>
              <Input value={form.genres} onChange={(event) => handleFormChange("genres", event.target.value)} />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label>Home city</Label>
                <Input value={form.home_city} onChange={(event) => handleFormChange("home_city", event.target.value)} />
              </div>
              <div>
                <Label>Home region</Label>
                <Input value={form.home_region} onChange={(event) => handleFormChange("home_region", event.target.value)} />
              </div>
              <div>
                <Label>Home country</Label>
                <Input value={form.home_country} onChange={(event) => handleFormChange("home_country", event.target.value)} />
              </div>
            </div>
            <div>
              <Label>Short bio</Label>
              <Textarea value={form.bio} onChange={(event) => handleFormChange("bio", event.target.value)} rows={4} />
            </div>
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">Members</span>
                <Button type="button" variant="ghost" size="sm" onClick={addMember}>
                  Add member
                </Button>
              </div>
              <div className="space-y-3">
                {members.map((member, index) => (
                  <div key={`${index}-${member.name}-${member.role}`} className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={member.name ?? ""}
                        onChange={(event) => handleMemberChange(index, "name", event.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Role/instrument</Label>
                      <Input
                        value={member.role ?? ""}
                        onChange={(event) => handleMemberChange(index, "role", event.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Website</Label>
                <Input value={form.website} onChange={(event) => handleFormChange("website", event.target.value)} />
              </div>
              <div>
                <Label>Booking email</Label>
                <Input value={form.booking_email} onChange={(event) => handleFormChange("booking_email", event.target.value)} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label>Instagram</Label>
                <Input value={form.instagram} onChange={(event) => handleFormChange("instagram", event.target.value)} />
              </div>
              <div>
                <Label>Facebook</Label>
                <Input value={form.facebook} onChange={(event) => handleFormChange("facebook", event.target.value)} />
              </div>
              <div>
                <Label>X</Label>
                <Input value={form.x_url} onChange={(event) => handleFormChange("x_url", event.target.value)} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label>YouTube</Label>
                <Input value={form.youtube_url} onChange={(event) => handleFormChange("youtube_url", event.target.value)} />
              </div>
              <div>
                <Label>TikTok</Label>
                <Input value={form.tiktok} onChange={(event) => handleFormChange("tiktok", event.target.value)} />
              </div>
              <div>
                <Label>Spotify</Label>
                <Input value={form.spotify_url} onChange={(event) => handleFormChange("spotify_url", event.target.value)} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label>Apple Music</Label>
                <Input
                  value={form.apple_music_url}
                  onChange={(event) => handleFormChange("apple_music_url", event.target.value)}
                />
              </div>
              <div>
                <Label>Bandcamp</Label>
                <Input value={form.bandcamp_url} onChange={(event) => handleFormChange("bandcamp_url", event.target.value)} />
              </div>
              <div>
                <Label>Bandsintown</Label>
                <Input
                  value={form.bandsintown_url}
                  onChange={(event) => handleFormChange("bandsintown_url", event.target.value)}
                />
              </div>
            </div>
            <section className="space-y-4 rounded-2xl border border-border/60 bg-background/30 p-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Assets</h3>
                <p className="text-xs text-muted-foreground">
                  Upload a high-res band photo plus a logo (SVG or JPG/PNG) for press kits and profiles.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="band-photo-upload">Band photo</Label>
                  <Input
                    id="band-photo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => handleBandPhotoUpload(event.target.files?.[0])}
                    disabled={uploadingBandPhoto}
                  />
                  {form.band_photo_url && (
                    <img
                      src={form.band_photo_url}
                      alt="Band photo"
                      className="h-32 w-full rounded-xl border border-border object-cover"
                    />
                  )}
                  <p className="text-xs text-muted-foreground">PNG, JPG, or WEBP · up to 10MB.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo-upload">Logo</Label>
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/svg+xml,image/png,image/jpeg,image/webp"
                    onChange={(event) => handleLogoUpload(event.target.files?.[0])}
                    disabled={uploadingLogo}
                  />
                  {form.logo_url && (
                    <img
                      src={form.logo_url}
                      alt="Artist logo"
                      className="h-32 w-full rounded-xl border border-border object-contain bg-background/70 p-3"
                    />
                  )}
                  <p className="text-xs text-muted-foreground">SVG, JPG, PNG, or WEBP · up to 5MB.</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Video links (up to 3)</Label>
                  {videoLinks.map((link, index) => (
                    <Input
                      key={`video-${index}`}
                      value={link}
                      onChange={(event) => handleVideoLinkChange(index, event.target.value)}
                      placeholder={`Video link ${index + 1}`}
                    />
                  ))}
                </div>
                <div className="space-y-2">
                  <Label>Audio links (up to 3)</Label>
                  {audioLinks.map((link, index) => (
                    <Input
                      key={`audio-${index}`}
                      value={link}
                      onChange={(event) => handleAudioLinkChange(index, event.target.value)}
                      placeholder={`Audio link ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </section>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="bandsintown-artist-page">Bandsintown Artist Page</Label>
                <Input
                  id="bandsintown-artist-page"
                  value={form.bandsintown_identifier}
                  onChange={(event) => handleFormChange("bandsintown_identifier", event.target.value)}
                  placeholder="https://www.bandsintown.com/a/<artist-id>-<artist-slug>"
                  aria-describedby="bandsintown-artist-page-help"
                />
                <p id="bandsintown-artist-page-help" className="mt-1 text-xs text-muted-foreground">
                  Paste your Bandsintown artist page URL.
                </p>
              </div>
              <div>
                <Label>Billing name</Label>
                <Input value={form.billing_name} onChange={(event) => handleFormChange("billing_name", event.target.value)} />
              </div>
            </div>
            <fieldset className="space-y-3 border-t border-border pt-4">
              <legend className="text-sm font-semibold text-foreground/80">Billing address</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Street address</Label>
                  <Input
                    value={billingAddress.line1}
                    onChange={(event) => handleBillingChange("line1", event.target.value)}
                  />
                </div>
                <div>
                  <Label>Address line 2</Label>
                  <Input
                    value={billingAddress.line2}
                    onChange={(event) => handleBillingChange("line2", event.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label>City</Label>
                  <Input value={billingAddress.city} onChange={(event) => handleBillingChange("city", event.target.value)} />
                </div>
                <div>
                  <Label>Region</Label>
                  <Input value={billingAddress.region} onChange={(event) => handleBillingChange("region", event.target.value)} />
                </div>
                <div>
                  <Label>Postal code</Label>
                  <Input
                    value={billingAddress.postal_code}
                    onChange={(event) => handleBillingChange("postal_code", event.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Country</Label>
                <Input value={billingAddress.country} onChange={(event) => handleBillingChange("country", event.target.value)} />
              </div>
            </fieldset>
            <div>
              <Label>Tax ID</Label>
              <Input value={form.tax_id} onChange={(event) => handleFormChange("tax_id", event.target.value)} />
            </div>
            {assetError && <p className="text-xs text-rose-400">{assetError}</p>}
            {formError && <p className="text-xs text-rose-400">{formError}</p>}
            {statusMessage && <p className="text-xs text-emerald-300">{statusMessage}</p>}
            <div className="flex justify-end">
              <Button type="submit" disabled={saving || !canSave}>
                {saving ? "Saving…" : "Save artist"}
              </Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
